<script setup lang="ts">
import {
  ContentsContext,
  Feature,
  PageName,
  SummaryInfo,
} from '@/api/tracking';
import CitationSourceBadge from '@/components/survey/generate-content/citation-source-badge.vue';
import SurveyContentRenderer from '@/components/survey/generate-content/survey-content-renderer.vue';
import { Part, SurveyHistoryStatus } from '@/types';
import TableWithDownload from './table-with-download.vue';

interface Props {
  contentParts: Array<Part>;
  enableAnimation: boolean;
  pageName: PageName;
  feature: Feature;
  summaryInfo?: SummaryInfo;
  contentsContext?: ContentsContext;
  status: SurveyHistoryStatus;
}

const props = defineProps<Props>();
</script>

<template>
  <template v-for="(part, i) in props.contentParts" :key="i">
    <span
      v-if="part.type === 'text'"
      :class="{ typewriter: props.enableAnimation }"
      :style="props.enableAnimation ? { animationDelay: `${part.delay}s` } : {}"
    >
      {{ part.content }}
    </span>
    <span
      v-else-if="part.type === 'citationSourceBadge'"
      :class="{ typewriter: props.enableAnimation }"
      :style="props.enableAnimation ? { animationDelay: `${part.delay}s` } : {}"
    >
      <CitationSourceBadge
        :badge-number="part.badgeNumber"
        :doc-type="part.docType"
        :source-document="part.sourceDocument"
        :page-name
        :feature
        :summary-info
        :contents-context
      />
    </span>
    <template v-else-if="part.type === 'tableWithDownload'">
      <TableWithDownload :key="`table-${i}`" :status="props.status">
        <component :is="part.tagName" v-bind="part.attributes">
          <SurveyContentRenderer
            v-if="part.children"
            :key="`table-children-${i}`"
            :content-parts="part.children"
            :enable-animation
            :page-name
            :feature
            :summary-info
            :contents-context
            :status
          />
        </component>
      </TableWithDownload>
    </template>
    <component
      v-else
      :is="part.tagName"
      v-bind="part.attributes"
      :style="props.enableAnimation ? { animationDelay: `${part.delay}s` } : {}"
      :class="{ typewriter: props.enableAnimation }"
    >
      <SurveyContentRenderer
        :key="`children-${i}`"
        v-if="part.children"
        :content-parts="part.children"
        :enable-animation
        :page-name
        :feature
        :summary-info
        :contents-context
        :status
      />
    </component>
  </template>
</template>

<style lang="scss" scoped>
h1,
h2,
h3,
h4,
h5,
h6 {
  font-family: 'NotoSansCJKjp-Bold';
  margin: 20px 0 8px;
  font-size: var(--survey-heading-font-size, 16px);
}

p,
li {
  font-size: var(--survey-text-font-size, 16px);
  line-height: 1.7;
}

li {
  margin: 8px 0 8px 2em;
}

strong {
  font-family: 'NotoSansCJKjp-Bold';
  font-weight: bold;
}

// パーサー(domParser.ts)側でタグ名で判定してクラスを付与するロジックを入れたくなかったため、描画側のスタイルで対応
table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  font-size: 16px;
  line-height: 1.7;
  background-color: $color-white;
}

thead th {
  background-color: $color-gray200;
  font-family: 'NotoSansCJKjp-Medium';
  font-weight: 700;
  border: 1px solid $color-gray400;
  padding: 8px;
  text-align: center;
  // 基本は`table-layout: auto;`の幅に任せるが、ヘッダーの最後の列に「参考」や「出典」が出力される場合に、2文字でも改行されてしまうパターンがあるため、最低限の幅を設定
  min-width: 50px;
}

tbody td {
  border: 1px solid $color-gray400;
  padding: 8px;
  text-align: left;
}

thead th,
tbody td {
  max-width: 300px;
  overflow-wrap: break-word;
}

pre {
  line-height: 1.7;
  overflow-x: auto;
}

.typewriter {
  animation: text-animation 0.15s ease forwards;
  opacity: 0;
}

@keyframes text-animation {
  to {
    opacity: 1;
  }
}
</style>
